import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router'

import { Button, Input, Modal } from '@ui/components/core'
import { MenuItem, TableList } from '@ui/components/elements/TableList/TableList'
import { Spinner } from '@ui/components/loaders'
import { Member } from '@ui/interfaces'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { useSettings } from '@ui/providers/SettingsProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import { CreateTeamInput, Team, teamService } from '@ui/services'

import { TeamDetails } from './components/TeamDetails'

export const Teams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const teamId = searchParams.get('teamId')

  const isQueryRailsLive = useFeatureFlag(FeatureFlagName.QueryRailsLive)

  const { currentMember, selectedTeam, setSelectedTeam } = useSettings()

  const [teams, setTeams] = useState<Team[]>([])
  const [teamName, setTeamName] = useState('')
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const { data: teamData, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamService.getTeams(currentMember as Member),
  })

  const { mutateAsync: createTeam, isPending: isCreatingTeam } = useMutation({
    mutationFn: (data: CreateTeamInput) => teamService.createTeam(data, currentMember as Member),
    onSuccess: (team) => {
      setTeams([...teams, team.data])

      setIsCreateTeamModalOpen(false)
      setTeamName('')
      setLoading(false)
    },
    onError: (error) => {
      console.error('Failed to create team', error)
    },
  })

  const { mutateAsync: updateTeam } = useMutation({
    mutationFn: (data: CreateTeamInput) =>
      teamService.updateTeam(selectedTeam?.id ?? '', data, currentMember as Member),
    onSuccess: (team) => {
      setTeams(teams.map((t) => (t.id === selectedTeam?.id ? team.data : t)))

      setIsCreateTeamModalOpen(false)
    },
    onError: (error) => {
      console.error('Failed to update team', error)
    },
  })

  const { mutateAsync: deleteTeam } = useMutation({
    mutationFn: (teamId: string) => teamService.deleteTeam(teamId.toString(), currentMember as Member),
    onSuccess: (_, teamId) => {
      const team = teams.find((team) => team.id === teamId)
      if (team) {
        setTeams(teams.filter((t) => t.id !== team.id))
      }
    },
    onError: (error) => {
      console.error('Failed to delete team', error)
    },
  })

  const onRowClick = (id: string) => {
    const team = teams.find((team) => team.id === id)
    if (team) {
      setSelectedTeam(team)
      setSearchParams({ teamId: team.id })
      setTeamName(team.name)
    }
  }

  const onClose = () => {
    setSelectedTeam(null)
    setTeamName('')
    setIsCreateTeamModalOpen(false)
  }

  const menuItems: MenuItem[] = [{ label: 'Delete', onClick: (id: string) => deleteTeam(id) }]

  useEffect(() => {
    setTeams(teamData ?? [])
  }, [teamData])

  useEffect(() => {
    setLoading(isLoading || isCreatingTeam)
  }, [isLoading, isCreatingTeam])

  useEffect(() => {
    if (teamId && teamId !== selectedTeam?.id) {
      const team = teams.find((team) => team.id === teamId)
      if (team) setSelectedTeam(team)
    }
  }, [selectedTeam, selectedTeam?.id, setSelectedTeam, teamId, teams])

  if (!isQueryRailsLive) {
    return <Navigate to={RouteNamesEnum.EVENTS_EXPLORER} />
  }

  return (
    <div className="size-full flex flex-col space-y-4">
      {selectedTeam ? (
        <TeamDetails />
      ) : (
        <>
          <div className="flex flex-row items-center justify-between space-x-2">
            <div className="flex flex-col space-y-1">
              <h1 className="text-xl font-bold">Teams</h1>
              <p className="text-sm text-gray-500">Manage your teams and their permissions.</p>
            </div>
            <Button onClick={() => setIsCreateTeamModalOpen(true)}>Create</Button>
          </div>

          <Modal title={selectedTeam ? 'Update Team' : 'Create Team'} isOpen={isCreateTeamModalOpen} onClose={onClose}>
            <form className="flex flex-col space-y-2 px-6 py-4" onSubmit={(e) => e.preventDefault()}>
              <Input
                hiddenLabel
                label="Name"
                placeholder="Team Name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
              />

              <div className="flex items-center justify-end space-x-2">
                <Button variant="tertiary" onClick={() => setIsCreateTeamModalOpen(false)}>
                  Cancel
                </Button>
                {selectedTeam ? (
                  <Button onClick={() => updateTeam({ name: teamName })}>Update</Button>
                ) : (
                  <Button onClick={() => createTeam({ name: teamName })}>Create</Button>
                )}
              </div>
            </form>
          </Modal>

          <div>
            {loading && <Spinner />}
            {teams && teams.length === 0 && !loading && <p className="text-sm text-gray-500">No teams found.</p>}
            {teams && teams.length >= 1 && !loading && (
              <TableList
                items={teams.map((team) => ({
                  id: team.id,
                  name: team.name,
                  members: team.memberships?.length ?? 0,
                }))}
                menuItems={menuItems}
                onRowClick={onRowClick}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
